import { Link } from "react-router-dom";
import { ArrowRight } from "lucide-react";
import { Reveal } from "@/components/Reveal";

const SHADES = [
  { name: "Mocha", hex: "#8A6247", body: "The signature. Deep enough to ground an outfit, warm enough to soften one." },
  { name: "Taupe", hex: "#B8AFA6", body: "The neutral that never reads clinical. Quiet on purpose." },
  { name: "Beige", hex: "#E5DFD5", body: "Our beige is borrowed from oat milk and gallery walls — never from a pharmacy shelf." },
  { name: "Clay", hex: "#A96A44", body: "The newest member. Terracotta warmth, fired and grounded — the shade of late afternoon." },
];

export default function CraftPage() {
  return (
    <main data-testid="craft-page" className="bg-[#EFE8DC] text-[#2C241E] pt-24">
      <section className="max-w-7xl mx-auto px-6 md:px-10 py-16 md:py-24">
        <Reveal>
          <p className="text-xs font-medium tracking-[0.25em] uppercase text-[#2C241E]/50 mb-4">
            The Craft
          </p>
          <h1
            data-testid="craft-page-headline"
            className="font-display text-4xl sm:text-5xl md:text-7xl tracking-tighter leading-[1.02] text-[#2C241E] max-w-4xl"
          >
            Built Quietly. Worn Loudly.
          </h1>
          <p className="mt-6 text-base md:text-lg leading-relaxed text-[#2C241E]/70 max-w-2xl">
            A first look at how LIGATEN pieces are being made — the shade, the stitch, the fabric.
            The full reveal stays under wraps until launch.
          </p>
        </Reveal>

        <div className="mt-20">
          <Reveal>
            <p className="text-xs font-medium tracking-[0.25em] uppercase text-[#A07856] mb-6">
              01 / The Shade
            </p>
          </Reveal>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 md:gap-6">
            {SHADES.map((s, i) => (
              <Reveal key={s.name} delay={i * 0.08}>
                <div
                  data-testid={`craft-shade-${s.name.toLowerCase()}`}
                  className="rounded-2xl p-8 min-h-[220px] flex flex-col justify-end"
                  style={{ backgroundColor: s.hex }}
                >
                  <p className={`font-display text-2xl ${s.name === "Beige" ? "text-[#2C241E]" : "text-[#EFE8DC]"}`}>
                    {s.name}
                  </p>
                  <p className={`mt-2 text-sm leading-relaxed ${s.name === "Beige" ? "text-[#2C241E]/65" : "text-[#EFE8DC]/75"}`}>
                    {s.body}
                  </p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>

        <div className="mt-24 grid grid-cols-1 md:grid-cols-12 gap-12 items-center">
          <div className="md:col-span-6">
            <Reveal>
              <p className="text-xs font-medium tracking-[0.25em] uppercase text-[#A07856] mb-4">
                02 / The Stitch
              </p>
              <h2 className="font-display text-3xl md:text-5xl tracking-tighter text-[#2C241E]">
                Words Where Logos Used to Be.
              </h2>
              <p className="mt-5 text-base md:text-lg leading-relaxed text-[#2C241E]/70 max-w-xl">
                Every LIGATEN band carries a line you choose yourself — stitched, not printed, and
                never a billboard. Six lines exist. One of them is yours. Which ones, and how it
                works, stays under wraps until the teaser phase.
              </p>
            </Reveal>
          </div>
          <Reveal className="md:col-span-6" delay={0.15}>
            <div className="rounded-2xl overflow-hidden aspect-[4/3]">
              <img
                src="https://images.unsplash.com/photo-1643313260651-9c335822ecde?q=80&w=1600&auto=format&fit=crop"
                alt="Macro close-up of the compression knit"
                loading="lazy"
                className="w-full h-full object-cover"
              />
            </div>
            <p className="mt-3 text-[10px] font-medium tracking-[0.25em] uppercase text-[#2C241E]/40 text-center">
              03 / The Fabric — Performance-grade compression knit
            </p>
          </Reveal>
        </div>

        <Reveal delay={0.1}>
          <Link
            to="/join"
            data-testid="craft-join-link"
            className="mt-20 inline-flex items-center gap-2 text-xs md:text-sm font-medium tracking-[0.2em] uppercase text-[#2C241E]/70 transition-colors duration-200 hover:text-[#2C241E]"
          >
            Be first to see the reveal
            <ArrowRight size={16} strokeWidth={1.75} />
          </Link>
        </Reveal>
      </section>
    </main>
  );
}
